import { type PropsWithChildren, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Element } from "react-scroll";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import { ProductEnum } from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";

import { LenderResponseForm } from "./LenderResponseForm";

const LenderApplicationSwitcher = ({ children }: PropsWithChildren) => {
  return (
    <div className="my-6 flex flex-col gap-2">
      <div className="mb-3 text-base">Choose primary financing type</div>
      <div className="flex flex-row gap-2">{children}</div>
    </div>
  );
};

const LenderApplicationButtonSwitcher = ({
  children,
  isActive,
  sibling,
}: PropsWithChildren<{
  isActive: boolean;
  sibling?: NonNullable<ILenderApplicationExtended["eligibleSiblings"]>[number];
}>) => {
  const navigate = useNavigate();

  const { data: currentCompany } = useGetCurrentLenderCompanyQuery();
  const onClick = useCallback(() => {
    if (sibling)
      navigate(
        `/lender-company/${currentCompany?.id}/lender-application/${sibling.id}`,
      );
  }, [currentCompany?.id, navigate, sibling]);

  return (
    <Button
      className={cn("text-gray-600", {
        "bg-gray-200": isActive,
      })}
      variant={"ghost"}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

interface IFormSwitcherProps {
  lenderApplication: ILenderApplicationExtended;
}

export const FormSwitcher = ({ lenderApplication }: IFormSwitcherProps) => {
  const { product } = lenderApplication;

  const textByProduct: Record<ProductEnum, string> = {
    [ProductEnum.COMPANY_LOAN]: "Company loan",
    [ProductEnum.REAL_ESTATE]: "Real estate loan",
    [ProductEnum.LEASING]: "Leasing",
    [ProductEnum.FACTORING]: "Factoring",
  } as const;

  const eligibleSiblings = lenderApplication.eligibleSiblings ?? [];

  return (
    <Element name="scrollToBidsForm">
      {eligibleSiblings.length > 0 && (
        <LenderApplicationSwitcher>
          <LenderApplicationButtonSwitcher isActive>
            {textByProduct[product]}
          </LenderApplicationButtonSwitcher>
          {eligibleSiblings.map((sibling) => (
            <LenderApplicationButtonSwitcher
              key={sibling.id}
              sibling={sibling}
              isActive={false}
            >
              {textByProduct[sibling.product]}
            </LenderApplicationButtonSwitcher>
          ))}
        </LenderApplicationSwitcher>
      )}
      <LenderResponseForm lenderApplication={lenderApplication} />
    </Element>
  );
};
